import { type Registry as Services, service } from '@ember/service';
import type Transition from '@ember/routing/transition';
import ActionController from 'uplisting-frontend/pods/action/controller';
import { formatLocalDate, isInvalidDate } from 'uplisting-frontend/utils/date';
import PermittedRoute from 'uplisting-frontend/pods/base/permitted-route';
import { routerHelper } from 'uplisting-frontend/utils/router';

interface IParams {
  from?: string;
}

const CALENDAR_LIKE_ROUTES = ['monthly', 'calendar', 'action'];

export default abstract class SidebarRoute extends PermittedRoute {
  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('repositories/property-tag')
  propertyTagRepository!: Services['repositories/property-tag'];

  permission = 'action';

  abstract parentRoute: string;

  propertiesInclude?: string;

  async beforeModel(transition: Transition): Promise<void> {
    await super.beforeModel(transition);

    if (
      isInvalidDate(transition.to?.queryParams?.['from'] as string | undefined)
    ) {
      transition.abort();

      await this.router.replaceWith(this.parentRoute, {
        queryParams: {
          from: formatLocalDate(new Date()),
        },
      });
    }

    await Promise.all([
      this.propertyRepository.findAll(
        this.propertiesInclude
          ? { include: this.propertiesInclude }
          : undefined,
      ),
      this.propertyTagRepository.findAll(),
    ]);
  }

  model(params: IParams) {
    const controller = this.controllerFor(this.parentRoute) as ActionController;

    Object.assign(controller, params);

    return controller.fetchDataTask.perform(true);
  }

  resetController(
    controller: ActionController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (CALENDAR_LIKE_ROUTES.includes(routerHelper.targetRoute ?? '')) {
      // when transitioning from calendar alike routes(monthly, calendar, action) we don't want to unload the records
      // as they are used in those pages
      return;
    }

    this.store.unloadAll('airbnb-official-link');
    this.store.unloadAll('booking');
    this.store.unloadAll('calendar');
    this.store.unloadAll('changeover');
    this.store.unloadAll('property');
    this.store.unloadAll('property-tag');
    this.store.unloadAll('multi-unit');
  }
}
